import React from 'react';
import styled from 'styled-components';

/**
 * LEGACY overlay spinner — closed to new usage. See docs/design-system.md →
 * "Loading & Progress" → "Migration note". Use `LoadingState` from
 * `common/Spinner` for a region, `FullScreenLoadingOverlay` for a blocking
 * wait, or `<Spinner size="sm" />` inline.
 *
 * Two defaults were removed because they leaked this component's AI-image
 * origin into unrelated features: `Ico` used to default to the four-leaf-clover
 * `FeelingLuckyIcon` (shipped by the 35 of 51 call sites that never overrode
 * it) and `text` used to default to 'Gathering list of images...'. Both now
 * default to nothing, so a caller renders only what it actually asks for.
 */
type Props = {
  text?: string | React.ReactNode;
  Ico?: React.ReactNode;
  customStyle?: any;
  contentStyle?: React.CSSProperties;
  spinnerStyle?: React.CSSProperties;
  spinnerCircleStyle?: React.CSSProperties;
  textStyle?: React.CSSProperties;
  iconStyle?: React.CSSProperties;
  spinnerSize?: string;
  onHide?: () => void;
  hideText?: string;
};
const SpinningLoading = (props: Props) => {
  const {
    text,
    Ico,
    customStyle = {},
    contentStyle = {},
    spinnerStyle = {},
    spinnerCircleStyle = {},
    textStyle = {},
    iconStyle = {},
    spinnerSize,
    onHide,
    hideText = 'Hide',
  } = props;
  return (
    <Main style={{ ...customStyle }} data-testid="loading-spinner">
      <Content style={{ ...contentStyle }}>
        <SpinnerWrapper style={{ ...spinnerStyle }} spinnerSize={spinnerSize}>
          {Ico && <Icon style={{ ...iconStyle }}>{Ico}</Icon>}
          <Spinner style={{ ...spinnerCircleStyle }}></Spinner>
        </SpinnerWrapper>
        {text && <Text style={{ ...textStyle }}>{text}</Text>}
        {!!onHide && !!hideText && (
          <HideButton type="button" onClick={onHide}>
            {hideText}
          </HideButton>
        )}
      </Content>
    </Main>
  );
};
export default SpinningLoading;

// Always centred in its region. A `positionTop` prop used to pin the ring near
// the top of the region, which put it straight on top of whatever the first
// child was — in the AI Producer rail that meant the Generate buttons the user
// had just clicked. The scrim is near-opaque on purpose: at the old 60% the
// content stayed legible underneath and the result read as two overlapping
// screens rather than one loading state.
// `border-radius: inherit` so the scrim takes the shape of whatever it covers.
// It had none, so a near-opaque rectangle painted over the corners of every
// rounded region it sits in — the version dropdown, a file list row, a B-roll
// tile — and while the wait ran those panels read as having lost their corners.
// None of those parents clip, and adding `overflow: hidden` to each would clip
// their menus too; inheriting resolves to `0` on a square parent, so the ~25
// call sites with one render exactly as before.
const Main = styled.div`
  position: absolute;
  background-color: color-mix(in srgb, var(--background) 92%, transparent);
  border-radius: inherit;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
`;

// Matches LoadingMessage in common/Spinner — the canonical loading label. Was
// a raw 16px bold, off the type scale entirely, and since this component is
// the most-rendered loading UI in the app that made the off-standard treatment
// the dominant one.
const Text = styled.div`
  color: var(--foreground);
  text-align: center;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
`;

// Ghost button, sm — the design system's example for exactly this kind of
// low-priority control. Was a styled.div coloured --primary and underlined:
// not focusable, not announced as actionable, and claiming the green reserved
// for the primary action while being the least important thing on screen.
const HideButton = styled.button`
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  border: none;
  border-radius: var(--radius-button);
  background: none;
  color: var(--text-muted);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;

  &:hover {
    background: var(--border);
    color: var(--text-secondary);
  }
`;

const SpinnerWrapper = styled.div<{ spinnerSize?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${props => props.spinnerSize || '72px'};
  height: ${props => props.spinnerSize || '72px'};
  position: relative;
`;

const Spinner = styled.div`
  flex-shrink: 0;
  width: 100%;
  height: 100%;
  border: 5px solid var(--border);
  border-top: 5px solid var(--primary);
  border-left: 5px solid var(--primary);
  border-right: 5px solid var(--primary);
  border-radius: 50%;
  animation: spin 2s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Icon = styled.div`
  position: absolute;
  transform: translate(-50%, -50%);
  top: calc(50%);
  left: calc(50%);
  line-height: 0;
`;
